import React, { useState, useContext, useEffect } from 'react';
import { AuthContext } from '../context/AuthContext';
import { userService } from '../api/userService';
import { grievanceService } from '../api/grievanceService';
import './AdminPanel.css';

/* ─────────────────────────────────────────────────────────────
   PS_BY_LEVEL — Kanpur Police, L-1 through L-6
───────────────────────────────────────────────────────────── */
const PS_BY_LEVEL = {
  'L-1': [
'AHTU','Anwarganj','Armapur','Araul','Babupurwa','Badshahi Naka','Bajaria','Bara','Bekanganj','Bidhnu','Bilhaur',
'Bithoor','Chakeri','Chamanganj','Chaubepur','Chhawani','Collectorganj','Colonelganj','Cyber Thana /Cyber Crime Cell','Fazalganj',
'Ghatampur','Govindnagar','Gujaini','Gwaltoli','Hanumant Vihar','Harbansh Mohal','Jajmau','Juhi','Kakadeo','Kakwan',
'Kalyanpur','Kidwainagar','Kohna','Kotwali','Maharajpur','Mahila Thana','Moolganj','Narwal','Nawabganj','Naubasta','Nazeerabad','Panki','Pheelkhana','Rail Bazar',
'Raipurwa','Rawatpur','Reuna','Saadh','Sachendi','Sajeti','Sen Paschim Para','Shivrajpur','Sisamau','Swaroopnagar'
  ],
  'L-2': [
    'ACP Kotwali', 'ACP Collectorganj', 'ACP Cantt', 'ACP Chakeri', 'ACP Crime',
    'ACP Panki', 'ACP Kalyanpur', 'ACP Bilhaur', 'ACP Babupurwa', 'ACP Naubasta',
    'ACP Ghatampur', 'ACP Colonelganj', 'ACP Sisamau', 'ACP Anwarganj',
    'ACP Swaroop Nagar'
  ],
  'L-3': ['ADCP East', 'ADCP West', 'ADCP South', 'ADCP Central', 'ADCP Crime', 'ADCP Traffic', 'ADCP CAW', 'ADCP Line'],
  'L-4': ['DCP Traffic', 'DCP HQ', 'DCP East', 'DCP West', 'DCP South', 'DCP Central', 'DCP Crime'],
  'L-5': [
    'Additional Commissioner of Police (Law & Order)',
    'Additional Commissioner of Police (Crime/HQ)'
  ],
  'L-6': ['Commissioner of Police']
};

const ALL_LEVELS   = ['L-1', 'L-2', 'L-3', 'L-4', 'L-5', 'L-6'];
const ALL_STATUSES = ['Pending', 'Satisfied', 'Not Satisfied', 'Miscellaneous'];

/* ─────────────────────────────────────────────────────────────
   AdminPanel
───────────────────────────────────────────────────────────── */
function AdminPanel() {
  const { user } = useContext(AuthContext);
  const [activeSection, setActiveSection] = useState('users');
  const [loading, setLoading] = useState(false);

  // Users state
  const [users, setUsers]               = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser]   = useState(null);
  const [selectedUserId, setSelectedUserId] = useState(null);

  // Complaints state
  const [complaints, setComplaints]               = useState([]);
  const [filteredComplaints, setFilteredComplaints] = useState([]);
  const [showComplaintForm, setShowComplaintForm] = useState(false);
  const [editingComplaint, setEditingComplaint]   = useState(null);
  const [selectedComplaintId, setSelectedComplaintId] = useState(null);
  const [complaintPage, setComplaintPage]             = useState(1);

  // Search & filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    userRole: 'all', userStatus: 'all', userPS: 'all',
    complaintLevel: 'all', complaintStatus: 'all', complaintPS: 'all',
    complaintDateFrom: '', complaintDateTo: ''
  });

  const [availableFilters, setAvailableFilters] = useState({
    userRoles: [], userPoliceStations: [],
    complaintLevels: ALL_LEVELS,
    complaintStatuses: ALL_STATUSES,
    complaintPoliceStations: []
  });

  /* ── fetch on section change ── */
  useEffect(() => {
    activeSection === 'users' ? fetchUsers() : fetchComplaints();
    setComplaintPage(1);
  }, [activeSection]);

  /* ── refilter whenever data/search/filters change ── */
  useEffect(() => {
    activeSection === 'users' ? filterUsers() : filterComplaints();
    setComplaintPage(1);
  }, [searchTerm, filters, users, complaints, activeSection]);

  /* ── build filter option lists ── */
  useEffect(() => {
    if (users.length > 0) {
      setAvailableFilters(prev => ({
        ...prev,
        userRoles: ['all', ...new Set(users.map(u => u.role))],
        userPoliceStations: ['all', ...new Set(users.map(u => u.currentPS).filter(Boolean))]
      }));
    }
  }, [users]);

  useEffect(() => {
    if (complaints.length > 0) {
      setAvailableFilters(prev => ({
        ...prev,
        complaintPoliceStations: ['all', ...new Set(complaints.map(c => c.currentPS).filter(Boolean))]
      }));
    }
  }, [complaints]);

  /* ── filter helpers ── */
  const filterUsers = () => {
    let r = [...users];
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      r = r.filter(u =>
        u.name?.toLowerCase().includes(t) || u.email?.toLowerCase().includes(t) ||
        u.mobile?.includes(t) || u.currentPS?.toLowerCase().includes(t) ||
        u.role?.toLowerCase().includes(t)
      );
    }
    if (filters.userRole   !== 'all') r = r.filter(u => u.role === filters.userRole);
    if (filters.userStatus !== 'all') r = r.filter(u => u.isActive === (filters.userStatus === 'active'));
    if (filters.userPS     !== 'all') r = r.filter(u => u.currentPS === filters.userPS);
    setFilteredUsers(r);
  };

  const filterComplaints = () => {
    let r = [...complaints];
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      r = r.filter(c =>
        c.name?.toLowerCase().includes(t) || c.mobile?.includes(t) ||
        c.description?.toLowerCase().includes(t) || c.currentPS?.toLowerCase().includes(t) ||
        c.status?.toLowerCase().includes(t) || c.enquiryOfficerName?.toLowerCase().includes(t)
      );
    }
    if (filters.complaintLevel  !== 'all') r = r.filter(c => c.level === filters.complaintLevel);
    if (filters.complaintStatus !== 'all') r = r.filter(c => c.status === filters.complaintStatus);
    if (filters.complaintPS     !== 'all') r = r.filter(c => c.currentPS === filters.complaintPS);
    if (filters.complaintDateFrom) {
      const d = new Date(filters.complaintDateFrom);
      r = r.filter(c => new Date(c.firstReceiptDate) >= d);
    }
    if (filters.complaintDateTo) {
      const d = new Date(filters.complaintDateTo);
      r = r.filter(c => new Date(c.firstReceiptDate) <= d);
    }
    setFilteredComplaints(r);
  };

  const clearFilters = () => {
    setSearchTerm('');
    setFilters({
      userRole: 'all', userStatus: 'all', userPS: 'all',
      complaintLevel: 'all', complaintStatus: 'all', complaintPS: 'all',
      complaintDateFrom: '', complaintDateTo: ''
    });
  };

  const hasActiveFilter = searchTerm ||
    Object.values(filters).some(v => v !== 'all' && v !== '');

  /* ── API calls ── */
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await userService.getAllUsers();
      if (response?.success) {
        let data = response.data?.data || response.data || response.users || [];
        const transformed = data.map(u => ({
          id: u.id, name: u.name, email: u.email, role: u.role,
          mobile: u.mobile, currentPS: u.current_ps || u.currentPS,
          createdAt: new Date(u.created_at || Date.now()).toISOString().slice(0, 10),
          isActive: u.is_active !== undefined ? u.is_active : true
        }));
        setUsers(transformed);
        setFilteredUsers(transformed);
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      alert('Failed to load users: ' + (err.response?.data?.message || err.message));
    } finally { setLoading(false); }
  };

  const fetchComplaints = async () => {
    try {
      setLoading(true);
      const response = await grievanceService.getAllGrievances();
      if (response?.success) {
        let data = response.data?.data || response.data || response.grievances || [];
        const transformed = data.map(g => ({
          id: g.id, level: g.level,
          firstReceiptDate: g.first_receipt_date || g.firstReceiptDate,
          lastMarkedDate:   g.last_marked_date   || g.lastMarkedDate,
          name: g.name, mobile: g.mobile, description: g.description,
          currentPS: g.current_ps || g.currentPS,
          status: g.status, remarks: g.remarks || '',
          enquiryOfficerName: g.enquiry_officer_name || g.enquiryOfficerName || '',
          history: g.history || [],
          forwarded_from_level: g.forwarded_from_level,
          previous_ps: g.previous_ps
        }));
        setComplaints(transformed);
        setFilteredComplaints(transformed);
      }
    } catch (err) {
      console.error('Error fetching complaints:', err);
      alert('Failed to load complaints: ' + (err.response?.data?.message || err.message));
    } finally { setLoading(false); }
  };

  /* ── User CRUD ── */
  const addUser = async (userData) => {
    try {
      const res = await userService.createUser({
        name: userData.name, email: userData.email,
        password: userData.password || 'default123',
        role: userData.role, mobile: userData.mobile,
        current_ps: userData.currentPS
      });
      if (res.success) { await fetchUsers(); setShowUserForm(false); alert('User created successfully'); }
    } catch (err) { alert(err.response?.data?.message || 'Failed to create user'); }
  };

  const updateUser = async (id, userData) => {
    try {
      const res = await userService.updateUser(id, {
        name: userData.name, email: userData.email,
        role: userData.role, mobile: userData.mobile,
        current_ps: userData.currentPS,
        is_active: userData.isActive !== undefined ? userData.isActive : true
      });
      if (res.success) { await fetchUsers(); setEditingUser(null); alert('User updated successfully'); }
    } catch (err) { alert(err.response?.data?.message || 'Failed to update user'); }
  };

  const deleteUser = async (id) => {
    if (!window.confirm('Delete this user?')) return;
    try {
      const res = await userService.deleteUser(id);
      if (res.success) { await fetchUsers(); setSelectedUserId(null); alert('User deleted'); }
    } catch (err) { alert(err.response?.data?.message || 'Failed to delete user'); }
  };

  /* ── Complaint CRUD ── */
  const addComplaint = async (data) => {
    try {
      const res = await grievanceService.createGrievance({
        level: data.level, first_receipt_date: data.firstReceiptDate,
        name: data.name, mobile: data.mobile, description: data.description,
        current_ps: data.currentPS, status: data.status,
        remarks: data.remarks, 
        enquiry_officer_name: data.enquiryOfficerName
      });
      if (res.success) { await fetchComplaints(); setShowComplaintForm(false); alert('Complaint created'); }
    } catch (err) { alert('Failed to create complaint'); }
  };

  const updateComplaint = async (id, data) => {
    try {
      const res = await grievanceService.updateGrievance(id, {
        level: data.level, first_receipt_date: data.firstReceiptDate,
        name: data.name, mobile: data.mobile, description: data.description,
        current_ps: data.currentPS, status: data.status,
        remarks: data.remarks,
        enquiry_officer_name: data.enquiryOfficerName
      });
      if (res.success) { await fetchComplaints(); setEditingComplaint(null); alert('Complaint updated'); }
    } catch (err) { alert('Failed to update complaint'); }
  };

  const deleteComplaint = async (id) => {
    if (!window.confirm('Delete this complaint?')) return;
    try {
      const res = await grievanceService.deleteGrievance(id);
      if (res.success) { await fetchComplaints(); setSelectedComplaintId(null); alert('Complaint deleted'); }
    } catch (err) { alert('Failed to delete complaint'); }
  };

  const selectedUser      = users.find(u => u.id === selectedUserId) || null;
  const selectedComplaint = complaints.find(c => c.id === selectedComplaintId) || null;

  const complaintsPerPage = 50;
  const totalPages = Math.ceil(filteredComplaints.length / complaintsPerPage) || 1;
  const paginatedComplaints = filteredComplaints.slice((complaintPage - 1) * complaintsPerPage, complaintPage * complaintsPerPage);

  return (
    <div className="admin-wrapper">
      <main className="admin-main">

        {/* ── Section Tabs ── */}
        <div className="section-tabs">
          <button className={`section-tab ${activeSection==='users'?'active':''}`}      onClick={() => setActiveSection('users')}>👥 User Management</button>
          <button className={`section-tab ${activeSection==='complaints'?'active':''}`} onClick={() => setActiveSection('complaints')}>📋 Complaint Management</button>
        </div>

        {/* ── Search & Filters ── */}
        <div className="search-filter-section">
          <div className="search-box">
            <input
              type="text"
              placeholder={activeSection === 'users'
                ? 'Search users by name, email, mobile, PS...'
                : 'Search complaints by name, mobile, description, PS, enquiry officer...'}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && <button className="clear-search-btn" onClick={() => setSearchTerm('')}>✕</button>}
          </div>

          {activeSection === 'users' ? (
            <div className="filter-container">
              <div className="filter-group">
                <label>Role:</label>
                <select value={filters.userRole} onChange={e => setFilters({...filters, userRole: e.target.value})} className="filter-select">
                  {availableFilters.userRoles.map(r => <option key={r} value={r}>{r === 'all' ? 'All Roles' : r}</option>)}
                </select>
              </div>
              <div className="filter-group">
                <label>Status:</label>
                <select value={filters.userStatus} onChange={e => setFilters({...filters, userStatus: e.target.value})} className="filter-select">
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="filter-group">
                <label>Police Station:</label>
                <select value={filters.userPS} onChange={e => setFilters({...filters, userPS: e.target.value})} className="filter-select">
                  {availableFilters.userPoliceStations.map(ps => <option key={ps} value={ps}>{ps === 'all' ? 'All PS' : ps}</option>)}
                </select>
              </div>
            </div>
          ) : (
            <div className="filter-container">
              <div className="filter-group">
                <label>Level:</label>
                <select value={filters.complaintLevel} onChange={e => setFilters({...filters, complaintLevel: e.target.value})} className="filter-select">
                  <option value="all">All Levels</option>
                  {ALL_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
                </select>
              </div>
              <div className="filter-group">
                <label>Status:</label>
                <select value={filters.complaintStatus} onChange={e => setFilters({...filters, complaintStatus: e.target.value})} className="filter-select">
                  <option value="all">All Status</option>
                  {ALL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="filter-group">
                <label>Police Station:</label>
                <select value={filters.complaintPS} onChange={e => setFilters({...filters, complaintPS: e.target.value})} className="filter-select">
                  {availableFilters.complaintPoliceStations.map(ps => <option key={ps} value={ps}>{ps === 'all' ? 'All PS' : ps}</option>)}
                </select>
              </div>
              <div className="filter-group date-filter">
                <label>Date From:</label>
                <input type="date" value={filters.complaintDateFrom} onChange={e => setFilters({...filters, complaintDateFrom: e.target.value})} className="date-input" />
              </div>
              <div className="filter-group date-filter">
                <label>Date To:</label>
                <input type="date" value={filters.complaintDateTo} onChange={e => setFilters({...filters, complaintDateTo: e.target.value})} className="date-input" />
              </div>
            </div>
          )}

          {hasActiveFilter && (
            <button className="clear-filters-btn" onClick={clearFilters}>Clear All Filters</button>
          )}
        </div>

        {/* Results info */}
        {hasActiveFilter && (
          <div className="search-results-info">
            <span className="results-count">
              Showing {activeSection === 'users' ? filteredUsers.length : filteredComplaints.length} of{' '}
              {activeSection === 'users' ? users.length : complaints.length}
              {activeSection === 'users' ? ' users' : ' complaints'}
            </span>
            {searchTerm && <span className="search-term">Search: <strong>"{searchTerm}"</strong></span>}
          </div>
        )}

        {loading && (
          <div style={{textAlign:'center',padding:'2rem'}}>
            <div className="spinner"></div><p>Loading...</p>
          </div>
        )}

        {/* ── USER MANAGEMENT ── */}
        {!loading && activeSection === 'users' && (
          <>
            <div className="admin-header-section">
              <h2 className="admin-title">User Management</h2>
              <button className="btn-add-user" onClick={() => { setShowUserForm(true); setEditingUser(null); }}>+ Add New User</button>
            </div>
            <div className="admin-content">
              <div className="users-list-section">
                <UsersList users={filteredUsers} selectedUserId={selectedUserId} onSelect={setSelectedUserId} />
              </div>
              <div className="user-detail-section">
                <UserDetail user={selectedUser} onEdit={setEditingUser} onDelete={deleteUser} />
              </div>
            </div>
            <div className="stats-cards">
              <div className="stat-card"><div className="stat-value">{filteredUsers.length}</div><div className="stat-label">Showing</div></div>
              <div className="stat-card"><div className="stat-value">{users.length}</div><div className="stat-label">Total Users</div></div>
              <div className="stat-card"><div className="stat-value">{filteredUsers.filter(u => u.role==='admin').length}</div><div className="stat-label">Admins</div></div>
              <div className="stat-card"><div className="stat-value">{filteredUsers.filter(u => u.role==='officer').length}</div><div className="stat-label">Officers</div></div>
              <div className="stat-card"><div className="stat-value">{filteredUsers.filter(u => u.isActive).length}</div><div className="stat-label">Active</div></div>
            </div>
          </>
        )}

        {/* ── COMPLAINT MANAGEMENT ── */}
        {!loading && activeSection === 'complaints' && (
          <>
            <div className="admin-header-section">
              <h2 className="admin-title">Complaint Management</h2>
              <button className="btn-add-user" onClick={() => { setShowComplaintForm(true); setEditingComplaint(null); }}>+ Add New Complaint</button>
            </div>
            <div className="admin-content">
              <div className="users-list-section">
                <ComplaintsList 
                  complaints={paginatedComplaints} 
                  selectedComplaintId={selectedComplaintId} 
                  onSelect={setSelectedComplaintId} 
                  totalCount={filteredComplaints.length}
                  currentPage={complaintPage}
                  totalPages={totalPages}
                  onPageChange={setComplaintPage}
                />
              </div>
              <div className="user-detail-section">
                <ComplaintDetail complaint={selectedComplaint} onEdit={setEditingComplaint} onDelete={deleteComplaint} />
              </div>
            </div>
            <div className="stats-cards">
              <div className="stat-card"><div className="stat-value">{filteredComplaints.length}</div><div className="stat-label">Showing</div></div>
              <div className="stat-card"><div className="stat-value">{complaints.length}</div><div className="stat-label">Total</div></div>
              <div className="stat-card"><div className="stat-value">{filteredComplaints.filter(c=>c.status==='Pending').length}</div><div className="stat-label">Pending</div></div>
              <div className="stat-card"><div className="stat-value">{filteredComplaints.filter(c=>c.status==='Satisfied').length}</div><div className="stat-label">Satisfied</div></div>
              <div className="stat-card"><div className="stat-value">{filteredComplaints.filter(c=>c.status==='Not Satisfied').length}</div><div className="stat-label">Not Satisfied</div></div>
              <div className="stat-card"><div className="stat-value">{filteredComplaints.filter(c=>c.status==='Miscellaneous').length}</div><div className="stat-label">Misc</div></div>
            </div>
          </>
        )}
      </main>

      {/* Modals */}
      {(showUserForm || editingUser) && (
        <div className="modal-overlay">
          <div className="modal-content">
            <UserForm
              initial={editingUser}
              onCancel={() => { setShowUserForm(false); setEditingUser(null); }}
              onSubmit={data => editingUser ? updateUser(editingUser.id, data) : addUser(data)}
            />
          </div>
        </div>
      )}

      {(showComplaintForm || editingComplaint) && (
        <div className="modal-overlay">
          <div className="modal-content">
            <ComplaintForm
              initial={editingComplaint}
              onCancel={() => { setShowComplaintForm(false); setEditingComplaint(null); }}
              onSubmit={data => editingComplaint ? updateComplaint(editingComplaint.id, data) : addComplaint(data)}
            />
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   UsersList
───────────────────────────────────────────────────────────── */
function UsersList({ users, selectedUserId, onSelect }) {
  return (
    <div className="users-list-box">
      <h3 className="section-title">Users ({users.length})</h3>
      <div className="users-cards">
        {users.length === 0 && <div className="no-users">No users found matching your criteria.</div>}
        {users.map(u => (
          <div key={u.id} className={`user-card ${selectedUserId===u.id?'selected':''}`} onClick={() => onSelect(u.id)}>
            <div className="user-card-header">
              <div className="user-name">{u.name}</div>
              <div className={`user-role-badge ${u.role==='admin'?'admin':'officer'}`}>{u.role}</div>
              <div className={`user-status-badge ${u.isActive?'active':'inactive'}`}>{u.isActive?'Active':'Inactive'}</div>
            </div>
            <div className="user-email">{u.email}</div>
            <div className="user-meta"><span>📱 {u.mobile}</span><span>🏢 {u.currentPS}</span></div>
            <div className="user-date">Created: {u.createdAt}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   UserDetail
───────────────────────────────────────────────────────────── */
function UserDetail({ user, onEdit, onDelete }) {
  if (!user) return <div className="detail-box"><p className="select-message">Select a user to view details.</p></div>;
  return (
    <div className="detail-box">
      <h3 className="section-title">User Details</h3>
      <div className="detail-grid">
        {[
          ['ID', user.id], ['Name', user.name], ['Email', user.email],
          ['Mobile', user.mobile], ['Current PS', user.currentPS], ['Created At', user.createdAt]
        ].map(([label, value]) => (
          <div key={label} className="detail-field"><span className="field-label">{label}:</span> {value}</div>
        ))}
        <div className="detail-field">
          <span className="field-label">Role:</span>
          <span className={`role-badge ${user.role==='admin'?'admin':'officer'}`}>{user.role}</span>
        </div>
        <div className="detail-field">
          <span className="field-label">Status:</span>
          <span className={`status-badge ${user.isActive?'active':'inactive'}`}>{user.isActive?'Active':'Inactive'}</span>
        </div>
      </div>
      <div className="button-group-detail">
        <button className="btn-edit" onClick={() => onEdit(user)}>Edit User</button>
        <button className="btn-delete" onClick={() => onDelete(user.id)}>Delete User</button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   UserForm
───────────────────────────────────────────────────────────── */
function UserForm({ initial, onCancel, onSubmit }) {
  const isEdit = !!initial;
  const [name, setName]         = useState(initial?.name || '');
  const [email, setEmail]       = useState(initial?.email || '');
  const [password, setPassword] = useState('');
  const [role, setRole]         = useState(initial?.role || 'officer');
  const [mobile, setMobile]     = useState(initial?.mobile || '');
  const [currentPS, setCurrentPS] = useState(initial?.currentPS || '');
  const [isActive, setIsActive] = useState(initial?.isActive !== false);

  function submit() {
    if (!name || !email || !mobile || !currentPS) { alert('Please fill all required fields'); return; }
    if (!isEdit && !password) { alert('Please provide a password'); return; }
    onSubmit({ name, email, password, role, mobile, currentPS, isActive });
  }

  return (
    <div className="form-container">
      <h3 className="form-title">{isEdit ? 'Edit User' : 'Add New User'}</h3>
      <div className="form-grid">
        <div className="form-field"><label>User ID</label><input className="form-input" value={initial?.id || 'Auto-generated'} disabled /></div>
        <div className="form-field"><label>Full Name *</label><input className="form-input" value={name} onChange={e=>setName(e.target.value)} placeholder="Enter full name" /></div>
        <div className="form-field"><label>Email *</label><input type="email" className="form-input" value={email} onChange={e=>setEmail(e.target.value)} placeholder="user@kanpurpolice.in" /></div>
        {!isEdit && <div className="form-field"><label>Password *</label><input type="password" className="form-input" value={password} onChange={e=>setPassword(e.target.value)} placeholder="Enter password" /></div>}
        <div className="form-field">
          <label>Role *</label>
          <select className="form-select" value={role} onChange={e=>setRole(e.target.value)}>
            <option value="officer">Officer</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div className="form-field"><label>Mobile *</label><input type="tel" className="form-input" value={mobile} onChange={e=>setMobile(e.target.value)} placeholder="9876543210" /></div>
        <div className="form-field"><label>Current Police Station *</label><input className="form-input" value={currentPS} onChange={e=>setCurrentPS(e.target.value)} placeholder="Station name" /></div>
        {isEdit && (
          <div className="form-field">
            <label>Status</label>
            <div className="status-toggle">
              <button type="button" className={`toggle-btn ${isActive?'active':''}`} onClick={() => setIsActive(true)}>Active</button>
              <button type="button" className={`toggle-btn ${!isActive?'active':''}`} onClick={() => setIsActive(false)}>Inactive</button>
            </div>
          </div>
        )}
      </div>
      <div className="form-actions">
        <button className="btn-submit" onClick={submit}>{isEdit?'Update User':'Add User'}</button>
        <button className="btn-cancel" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   ComplaintsList
───────────────────────────────────────────────────────────── */
function ComplaintsList({ complaints, selectedComplaintId, onSelect, totalCount, currentPage, totalPages, onPageChange }) {
  return (
    <div className="users-list-box">
      <h3 className="section-title">Complaints ({totalCount})</h3>
      <div className="users-cards">
        {complaints.length === 0 && <div className="no-users">No complaints found matching your criteria.</div>}
        {complaints.map(c => (
          <div key={c.id} className={`user-card ${selectedComplaintId===c.id?'selected':''}`} onClick={() => onSelect(c.id)}>
            <div className="user-card-header">
              <div className="user-name">{c.name}</div>
              <div className={`level-badge level-tag`}>{c.level}</div>
              <div className={`status-badge status-${c.status.toLowerCase().replace(/ /g,'-')}`}>{c.status}</div>
            </div>
            <div className="user-email complaint-desc">{c.description}</div>
            <div className="user-meta">
              <span>📱 {c.mobile}</span>
              <span>🏢 {c.currentPS}</span>
              <span>📅 {new Date(c.firstReceiptDate).toLocaleDateString()}</span>
            </div>
            {c.enquiryOfficerName && (
              <div className="enquiry-officer-tag">🔍 Enquiry: {c.enquiryOfficerName}</div>
            )}
          </div>
        ))}
      </div>
      {totalPages > 1 && (
        <div className="pagination-container">
          <button 
            disabled={currentPage === 1} 
            onClick={() => onPageChange(currentPage - 1)}
            className="btn-pagination"
          >
            ◀ Prev
          </button>
          <span className="pagination-info">
            Page {currentPage} of {totalPages}
          </span>
          <button 
            disabled={currentPage >= totalPages} 
            onClick={() => onPageChange(currentPage + 1)}
            className="btn-pagination"
          >
            Next ▶
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   ComplaintDetail  ← shows all fields including enquiry officer
───────────────────────────────────────────────────────────── */
function ComplaintDetail({ complaint, onEdit, onDelete }) {
  if (!complaint) return <div className="detail-box"><p className="select-message">Select a complaint to view details.</p></div>;

  return (
    <div className="detail-box">
      <h3 className="section-title">Complaint Details</h3>

      <div className="detail-grid">
        <div className="detail-field"><span className="field-label">Sr. No:</span> {complaint.id}</div>
        <div className="detail-field">
          <span className="field-label">Level:</span>
          <span className="level-badge level-tag">{complaint.level}</span>
        </div>
        <div className="detail-field">
          <span className="field-label">Status:</span>
          <span className={`status-badge status-${complaint.status.toLowerCase().replace(/ /g,'-')}`}>{complaint.status}</span>
        </div>
        <div className="detail-field"><span className="field-label">Name:</span> {complaint.name}</div>
        <div className="detail-field"><span className="field-label">Mobile:</span> {complaint.mobile}</div>
        <div className="detail-field"><span className="field-label">Current PS:</span> {complaint.currentPS}</div>
        <div className="detail-field"><span className="field-label">First Receipt:</span> {new Date(complaint.firstReceiptDate).toLocaleDateString()}</div>
        <div className="detail-field"><span className="field-label">Last Marked:</span> {complaint.lastMarkedDate ? new Date(complaint.lastMarkedDate).toLocaleDateString() : 'N/A'}</div>

<div className="detail-field">
  <span className="field-label">🔍 Enquiry Officer:</span>
  <span className="field-value">{complaint.enquiryOfficerName || '—'}</span>
</div>

        {/* Forwarded From */}
        {complaint.forwarded_from_level && (
          <div className="detail-field forwarded-detail">
            <span className="field-label">↗️ Forwarded From:</span>
            <span className="forwarded-value">{complaint.forwarded_from_level}{complaint.previous_ps && ` — ${complaint.previous_ps}`}</span>
          </div>
        )}
      </div>

      <div className="description-section-admin">
        <div className="field-label mb-4">Description:</div>
        <div className="description-box">{complaint.description}</div>
      </div>

      {complaint.remarks && (
        <div className="description-section-admin">
          <div className="field-label mb-4">Remarks:</div>
          <div className="description-box">{complaint.remarks}</div>
        </div>
      )}

      {/* History */}
      {complaint.history && complaint.history.length > 0 && (
        <div className="history-section-admin">
          <div className="field-label mb-4">History:</div>
          {complaint.history.map((h, i) => (
            <div key={i} className="history-item-admin">
              <div className="history-date-admin">{new Date(h.created_at).toLocaleString('en-IN')}</div>
              <div className="history-action-admin">
                {h.action}{h.action_by && ` by ${h.action_by}`}
                {h.from_ps && ` (from ${h.from_ps})`}{h.to_ps && ` → ${h.to_ps}`}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="button-group-detail">
        <button className="btn-edit" onClick={() => onEdit(complaint)}>Edit Complaint</button>
        <button className="btn-delete" onClick={() => onDelete(complaint.id)}>Delete Complaint</button>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────
   ComplaintForm  ← new field: Enquiry Officer Name
───────────────────────────────────────────────────────────── */
function ComplaintForm({ initial, onCancel, onSubmit }) {
  const isEdit = !!initial;
  const [level, setLevel]             = useState(initial?.level || 'L-1');
  const [date, setDate]               = useState(initial?.firstReceiptDate?.slice(0,10) || new Date().toISOString().slice(0,10));
  const [name, setName]               = useState(initial?.name || '');
  const [mobile, setMobile]           = useState(initial?.mobile || '');
  const [description, setDescription] = useState(initial?.description || '');
  const [currentPS, setCurrentPS]     = useState(initial?.currentPS || '');
  const [status, setStatus]           = useState(initial?.status || 'Pending');
  const [remarks, setRemarks]         = useState(initial?.remarks || '');
  const [enquiryOfficerName, setEnquiryOfficerName]   = useState(initial?.enquiryOfficerName || '');

  const stations = PS_BY_LEVEL[level] || [];

  // Reset PS when level changes
  useEffect(() => { if (!initial) setCurrentPS(''); }, [level]);

  function submit() {
    if (!name || !mobile || !description || !currentPS) { alert('Please fill all required fields'); return; }
    onSubmit({ level, firstReceiptDate: date, name, mobile, description, currentPS, status, remarks, enquiryOfficerName });
  }

  return (
    <div className="form-container">
      <h3 className="form-title">{isEdit ? 'Edit Complaint' : 'Add New Complaint'}</h3>
      <div className="form-grid">

        <div className="form-field">
          <label>Sr. No</label>
          <input className="form-input" value={initial?.id || 'Auto'} disabled />
        </div>

        <div className="form-field">
          <label>First Receipt Date *</label>
          <input type="date" className="form-input" value={date} onChange={e=>setDate(e.target.value)} />
        </div>

        <div className="form-field">
          <label>Level *</label>
          <select className="form-select" value={level} onChange={e=>setLevel(e.target.value)}>
            {ALL_LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>

        <div className="form-field">
          <label>Current Police Station *</label>
          <select className="form-select" value={currentPS} onChange={e=>setCurrentPS(e.target.value)}>
            <option value="">-- Select Station --</option>
            {stations.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="form-field">
          <label>Complainant Name *</label>
          <input className="form-input" value={name} onChange={e=>setName(e.target.value)} placeholder="Full name" />
        </div>

        <div className="form-field">
          <label>Mobile No. *</label>
          <input type="tel" className="form-input" value={mobile} onChange={e=>setMobile(e.target.value)} placeholder="9876543210" />
        </div>

        {/* ★ NEW FIELD */}
        <div className="form-field">
          <label>Name of Enquiry Officer</label>
          <input
            className="form-input enquiry-officer-input"
            value={enquiryOfficerName}
            onChange={e => setEnquiryOfficerName(e.target.value)}
            placeholder="e.g. Inspector Rajesh Kumar"
          />
        </div>

        <div className="form-field">
          <label>Status *</label>
          <select className="form-select" value={status} onChange={e=>setStatus(e.target.value)}>
            {ALL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        </div>

        <div className="form-field full-width">
          <label>Description *</label>
          <textarea className="form-textarea" rows={4} value={description} onChange={e=>setDescription(e.target.value)} placeholder="Complaint description..." />
        </div>

        <div className="form-field full-width">
          <label>Remarks</label>
          <textarea className="form-textarea" rows={3} value={remarks} onChange={e=>setRemarks(e.target.value)} placeholder="Optional remarks..." />
        </div>

      </div>
      <div className="form-actions">
        <button className="btn-submit" onClick={submit}>{isEdit?'Update Complaint':'Add Complaint'}</button>
        <button className="btn-cancel" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

export default AdminPanel;